import React, { useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import BorderStyle from '@resolvers/tools/BorderStyle/BorderStyle';
import AlignStyle from '@resolvers/tools/AlignStyle/AlignStyle';

import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import {
  BORDER_OPTIONS,
  EDITOR_RESOLVERS,
  H_ALIGN_OPTIONS,
  V_ALIGN_OPTIONS,
} from '@common/constants';
import ColorButtons from '@resolvers/tools/ColorButtons/ColorButtons';
import {
  isNodeEmailContainerColumn,
  isNodeEmailContainerRow,
} from '@common/helpers/helpers';

const EmailContainerColumn = ({
  children,
  width,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  borderLeft,
  borderBottom,
  borderTop,
  borderRight,
  vAlign,
  align,
  background,
  backgroundImage,
  backgroundSize,
  backgroundPosition,
}) => {
  const { actions } = useEditor();
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const ref = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (ref?.current) {
      connect(drag(ref.current));
    }
  }, []);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('container_column');
      });
    });
  }, [setCustom, t]);

  const backgroundImageProps = backgroundImage
    ? {
        backgroundImage: `url('${backgroundImage}')`,
        backgroundSize,
        backgroundPosition,
        backgroundRepeat: 'no-repeat',
      }
    : {};

  return (
    <td
      ref={ref}
      valign={vAlign}
      align={align}
      style={{
        width: `${width}%`,
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        maxWidth: '0',
        paddingLeft: `${paddingLeft || 0}px`,
        paddingRight: `${paddingRight || 0}px`,
        paddingTop: `${paddingTop || 0}px`,
        paddingBottom: `${paddingBottom || 0}px`,
        borderLeft: `${borderLeft ? '1px solid #e0e1e2' : ''}`,
        borderBottom: `${borderBottom ? '1px solid #e0e1e2' : ''}`,
        borderTop: `${borderTop ? '1px solid #e0e1e2' : ''}`,
        borderRight: `${borderRight ? '1px solid #e0e1e2' : ''}`,
        backgroundColor: background,
        ...backgroundImageProps,
      }}
    >
      {children}
    </td>
  );
};

const EmailContainerColumnConfigPanel = () => {
  return (
    <SettingsWrapper>
      <SettingsRow>
        <SizeStyle propKey='width' max={100} label='width' />
      </SettingsRow>
      <PaddingStyle />
      <SettingsRow>
        <AlignStyle propKey='vAlign' options={V_ALIGN_OPTIONS} />
        <AlignStyle propKey='align' options={H_ALIGN_OPTIONS} />
      </SettingsRow>
      <ColorButtons hide={{ textColor: true }} />
      <SettingsRow sx={{ mb: 3 }}>
        <BorderStyle options={BORDER_OPTIONS} />
      </SettingsRow>
      {/* <SettingsRow>
        <BackgroundImageStyle />
      </SettingsRow> */}
    </SettingsWrapper>
  );
};

const EmailContainerColumnProps = {
  width: '30',
  paddingLeft: '5',
  paddingRight: '5',
  paddingTop: '5',
  paddingBottom: '5',
  borderLeft: false,
  borderBottom: false,
  borderTop: false,
  borderRight: false,
  vAlign: 'top',
  align: 'left',
  background: 'transparent',
  backgroundImage: undefined,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  isDeletable: true,
};

EmailContainerColumn.propTypes = {
  children: PropTypes.node,
  width: PropTypes.string,
  paddingLeft: PropTypes.string,
  paddingRight: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
  borderLeft: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderTop: PropTypes.bool,
  borderRight: PropTypes.bool,
  vAlign: PropTypes.string,
  align: PropTypes.string,
  background: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundSize: PropTypes.string,
  backgroundPosition: PropTypes.string,
};

EmailContainerColumn.craft = {
  displayName: EDITOR_RESOLVERS.EMAIL.CONTAINER_COLUMN,
  name: EDITOR_RESOLVERS.EMAIL.CONTAINER_COLUMN,
  props: EmailContainerColumnProps,
  related: { settings: EmailContainerColumnConfigPanel },
  rules: {
    canMoveIn: (incomingNodes) => {
      return incomingNodes.every((node) => {
        return (
          !isNodeEmailContainerRow(node) && !isNodeEmailContainerColumn(node)
        );
      });
    },
  },
};

export default EmailContainerColumn;
